import type { Project } from '@/types'

import { useRef } from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { gsap, registerGsap, wfEase, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { Reveal } from '@/components/animation/reveal'

interface ProjectGridProps {
    projects: Project[]
    overlayWhite?: boolean
}

function ProjectCard({
    project,
    index,
    overlayWhite
}: {
    project: Project
    index: number
    overlayWhite?: boolean
}) {
    const overlayRef = useRef<HTMLDivElement>(null)

    // a-52: the cover panel sits over the image, then slides down to reveal it
    // top-to-bottom on scroll into view (1.5s inOutCubic, 0.3s delay).
    useIsomorphicLayoutEffect(() => {
        const el = overlayRef.current
        if (!el) return
        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { yPercent: 100 })
            return
        }

        const ctx = gsap.context(() => {
            gsap.set(el, { yPercent: 0, opacity: 1 })
            gsap.to(el, {
                yPercent: 100,
                duration: 1.5,
                delay: 0.3,
                ease: wfEase('inOutCubic'),
                scrollTrigger: { trigger: el, start: 'top 85%', once: true }
            })
        }, overlayRef)

        return () => ctx.revert()
    }, [])

    return (
        <div role='listitem' className='project-collection-item w-dyn-item'>
            <Link
                href={`/projects/${project.slug}`}
                className='project-item w-inline-block'
                data-cursor='big'
            >
                <div className='project-item-image-holder'>
                    <img
                        alt={project.name}
                        src={project.thumb}
                        className='project-item-image'
                    />
                    <div
                        ref={overlayRef}
                        className={cn(
                            'project-item-image-overlay',
                            overlayWhite && 'project-item-image-overlay-white'
                        )}
                        style={{ opacity: 1 }}
                    />
                </div>
                <div className='project-item-content'>
                    <Reveal
                        mode='view'
                        delay={index * 0.05}
                        innerClassName='perspective-load1-on-view'
                    >
                        <h3 className='project-item-title'>{project.name}</h3>
                    </Reveal>
                    <div className='project-item-arrow-holder'>
                        <img
                            src='/images/Link-Arrow.svg'
                            alt=''
                            className='project-item-arrow'
                        />
                    </div>
                </div>
            </Link>
        </div>
    )
}

export function ProjectGrid({ projects, overlayWhite }: ProjectGridProps) {
    return (
        <div className='w-dyn-list'>
            <div role='list' className='project-collection-list w-dyn-items'>
                {projects.map((project, index) => (
                    <ProjectCard
                        key={project.slug}
                        project={project}
                        index={index}
                        overlayWhite={overlayWhite}
                    />
                ))}
            </div>
        </div>
    )
}
